import { SeverityLevel } from "@sentry/nextjs";
import { getLogger, NQError, NQErrorCode } from "@visonum/network-quality-sdk";
import { LogCallback, LoggerLevel } from "@visonum/network-quality-sdk/dist/utils/logger";
import * as R from "ramda";
import packageJson from "../../package.json";
import { ProcessEnv } from "../config-types";
import { sentryReportsAddBreadcrumb } from "../features/sentryReports/sentryReportsSlice";
import { isRunningInBrowser } from "./ssr";
import { allowShowLog } from "./utils/allowShowLog";
import { getUrlParamValue } from "./utils/url";

const getUseLoggerFromUrl = (url: string | null) => !R.isNil(getUrlParamValue(url, "enable-logger"));

const isProductionMode = () => {
  const NODE_ENV = (process.env.NEXT_PUBLIC_NODE_ENV as ProcessEnv);
  return (R.isNil(NODE_ENV) || NODE_ENV === "production")
}

const getLoggerLevel = () => {
  const url = isRunningInBrowser() ? window.location.href : null;

  if (getUseLoggerFromUrl(url)) {
    return "trace";
  }

  return isProductionMode() ? "error" : "trace";
}

const loggerLevelToSeverity = (level: LoggerLevel): SeverityLevel => {
  switch (level) {
    case "silent":
      throw new NQError(NQErrorCode.Client, `LoggerLevel is out of order: ${level}`);
    case "trace": return "debug";
    case "debug": return "debug";
    case "info": return "info"
    case "warn": return "warning";
    case "error": return "error";
    case "fatal": return "fatal";
  }
}

const loggerCallback: LogCallback = ({ level, message, object }) => {
  if (level === "silent") {
    return;
  }

  if (isRunningInBrowser()) {
    sentryReportsAddBreadcrumb({
      category: "Logger",
      level: loggerLevelToSeverity(level),
      message: message,
      data: {
        object: object,
      }
    });
  }
}

const getLoggerInternal = () => {
  const logger = getLogger({
    name: packageJson?.name ?? "test",
    level: getLoggerLevel(),
    formatObjects: true,
    storeMessages: allowShowLog(),
    callback: isProductionMode() ? loggerCallback : undefined
  });

  return logger;
}

export const logger = getLoggerInternal();
